/**
 * AppTextInput Component
 *
 * A reusable text input wrapping Mantine TextInput with a custom label row
 * that displays label text, required asterisk, and character counter.
 * Handles paste events to trim text at maxLength boundary.
 *
 * @example
 * ```tsx
 * // Basic usage with character counter
 * <AppTextInput
 *   label="Task title"
 *   value={title}
 *   onChange={handleChange}
 *   maxLength={100}
 *   required
 * />
 *
 * // Without counter
 * <AppTextInput
 *   label="Email"
 *   value={email}
 *   onChange={handleChange}
 *   showCounter={false}
 * />
 *
 * // No label (plain TextInput behavior)
 * <AppTextInput
 *   value={value}
 *   onChange={handleChange}
 *   placeholder="Enter text..."
 * />
 * ```
 */

import type { ClipboardEvent, ChangeEvent } from 'react';
import { memo, useCallback } from 'react';
import { TextInput } from '@mantine/core';

import { InputLabel } from '@/components/common/InputLabel';
import type { AppTextInputProps } from './AppTextInput.types';
import styles from './AppTextInput.module.css';

export const AppTextInput = memo(function AppTextInput({
  label,
  maxLength,
  showCounter,
  required,
  value,
  onChange,
  className,
  onPaste,
  ...mantineProps
}: AppTextInputProps) {
  const currentLength = typeof value === 'string' ? value.length : 0;
  const shouldShowCounter = showCounter ?? maxLength !== undefined;

  const labelComponent = label ? (
    <InputLabel
      label={label}
      required={required}
      showCounter={shouldShowCounter}
      currentLength={currentLength}
      maxLength={maxLength}
    />
  ) : undefined;

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      if (maxLength) {
        const pastedText = event.clipboardData.getData('text');
        const inputElement = event.target as HTMLInputElement;
        const currentValue = inputElement.value;
        const selectionStart =
          inputElement.selectionStart ?? currentValue.length;
        const selectionEnd = inputElement.selectionEnd ?? selectionStart;
        const newValue =
          currentValue.slice(0, selectionStart) +
          pastedText +
          currentValue.slice(selectionEnd);

        if (newValue.length > maxLength) {
          event.preventDefault();
          const trimmedValue = newValue.slice(0, maxLength);
          inputElement.value = trimmedValue;
          onChange?.(trimmedValue as unknown as ChangeEvent<HTMLInputElement>);
        }
      }
      onPaste?.(event);
    },
    [maxLength, onChange, onPaste]
  );

  return (
    <TextInput
      {...mantineProps}
      label={labelComponent}
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      required={required}
      withAsterisk={false}
      onPaste={handlePaste}
      className={className}
      classNames={{ label: styles.fullWidthLabel }}
    />
  );
});

AppTextInput.displayName = 'AppTextInput';
